import {
  collection,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

/** Подколлекция: `users/{uid}/p2pContacts/{docId}` — контакты 1:1 в облаке (синхрон между устройствами). */
export const P2P_CONTACTS_SUBCOLLECTION = 'p2pContacts';

export type ChatContact = {
  id: string;
  name: string;
  addedAt: number;
  /** Firebase uid собеседника (пишем при первом известном inviteLookup / сообщении) — после входа с нового браузера хватает контакта без живого inviteLookup. */
  peerUid?: string;
  /** В архиве: не показывается в основном списке, переписку можно открыть из «Архива». */
  archived?: boolean;
};

/** Стабильный id документа: peer-токен может содержать символы, нежелательные для пути. */
export function peerInviteTokenToContactDocId(peerToken: string): string {
  const bytes = new TextEncoder().encode(peerToken);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function contactsCollectionRef(userId: string) {
  return collection(db, 'users', userId, P2P_CONTACTS_SUBCOLLECTION);
}

function docToContact(d: { id: string; data: () => Record<string, unknown> }): ChatContact | null {
  const data = d.data();
  const peer = data.peerInviteToken;
  const name = data.name;
  const addedAt = data.addedAt;
  const archivedRaw = data.archived;
  const peerUidRaw = data.peerUid;
  if (typeof peer !== 'string' || !peer.trim()) return null;
  if (typeof name !== 'string') return null;
  if (typeof addedAt !== 'number' || !Number.isFinite(addedAt)) return null;
  const archived = archivedRaw === true;
  const peerUid =
    typeof peerUidRaw === 'string' && peerUidRaw.trim().length > 0 ? peerUidRaw.trim() : undefined;
  const base = peerUid ? { id: peer, name, addedAt, peerUid } : { id: peer, name, addedAt };
  return archived ? { ...base, archived: true } : base;
}

export function subscribeChatContacts(
  userId: string,
  onUpdate: (contacts: ChatContact[]) => void,
  onError?: (e: Error) => void
): () => void {
  if (!userId) {
    onUpdate([]);
    return () => {};
  }
  const cref = contactsCollectionRef(userId);
  return onSnapshot(
    cref,
    (snap) => {
      const list: ChatContact[] = [];
      snap.forEach((d) => {
        const c = docToContact(d);
        if (c) list.push(c);
      });
      list.sort((a, b) => b.addedAt - a.addedAt);
      onUpdate(list);
    },
    (err) => {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    }
  );
}

/** Разовая выборка (удаление аккаунта и т.п.). */
export async function fetchP2PContactsOnce(userId: string): Promise<ChatContact[]> {
  if (!userId) return [];
  const snap = await getDocs(contactsCollectionRef(userId));
  const list: ChatContact[] = [];
  snap.forEach((d) => {
    const c = docToContact(d);
    if (c) list.push(c);
  });
  list.sort((a, b) => b.addedAt - a.addedAt);
  return list;
}

export async function deleteAllP2PContactsDocs(userId: string): Promise<void> {
  if (!userId) return;
  const cref = contactsCollectionRef(userId);
  const CHUNK = 400;
  for (;;) {
    const snap = await getDocs(query(cref, orderBy(documentId()), limit(CHUNK)));
    if (snap.empty) break;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    if (snap.size < CHUNK) break;
  }
}

/** Дописываем uid собеседника в контакт (идемпотентно). */
export async function mergeP2PContactPeerUid(
  userId: string,
  peerInviteToken: string,
  peerUid: string
): Promise<void> {
  const uid = peerUid.trim();
  if (!userId || !peerInviteToken.trim() || !uid) return;
  const token = peerInviteToken.trim();
  const ref = doc(
    db,
    'users',
    userId,
    P2P_CONTACTS_SUBCOLLECTION,
    peerInviteTokenToContactDocId(token)
  );
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const cur = snap.data().peerUid;
  if (cur === uid) return;
  await updateDoc(ref, { peerUid: uid });
}

async function tryMergePeerUidFromInviteLookup(userId: string, token: string): Promise<void> {
  try {
    const lk = await getDoc(doc(db, 'inviteLookup', token));
    const u = lk.exists() ? (lk.data() as { uid?: string }).uid : undefined;
    if (typeof u === 'string' && u.trim()) {
      await mergeP2PContactPeerUid(userId, token, u.trim());
    }
  } catch {
    /* сеть / правила */
  }
}

export async function addP2PContactFromInviteToken(
  inviteToken: string | null,
  userId: string,
  myInviteToken: string
): Promise<'added' | 'exists' | 'self' | 'invalid'> {
  if (!userId) return 'invalid';
  if (!inviteToken || !inviteToken.trim()) return 'invalid';
  const token = inviteToken.trim();
  const my = myInviteToken.trim();
  if (!my) return 'invalid';
  if (token === my) return 'self';

  const cref = contactsCollectionRef(userId);
  const q = query(cref, where('peerInviteToken', '==', token), limit(1));
  const found = await getDocs(q);
  if (!found.empty) {
    void tryMergePeerUidFromInviteLookup(userId, token);
    return 'exists';
  }

  const ref = doc(db, 'users', userId, P2P_CONTACTS_SUBCOLLECTION, peerInviteTokenToContactDocId(token));
  await setDoc(ref, {
    peerInviteToken: token,
    name: `Contact ${token.slice(0, 8)}…`,
    addedAt: Date.now(),
    archived: false,
  });
  void tryMergePeerUidFromInviteLookup(userId, token);
  return 'added';
}

export async function setChatContactArchived(
  userId: string,
  contactInviteToken: string,
  archived: boolean
): Promise<boolean> {
  if (!userId || !contactInviteToken) return false;
  const ref = doc(db, 'users', userId, P2P_CONTACTS_SUBCOLLECTION, peerInviteTokenToContactDocId(contactInviteToken));
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  await updateDoc(ref, { archived });
  return true;
}

export async function renameChatContact(userId: string, contactId: string, newName: string): Promise<boolean> {
  if (!userId || !contactId || !newName.trim()) return false;
  const ref = doc(db, 'users', userId, P2P_CONTACTS_SUBCOLLECTION, peerInviteTokenToContactDocId(contactId));
  const snap = await getDoc(ref);
  if (!snap.exists()) return false;
  await updateDoc(ref, { name: newName.trim() });
  return true;
}

export async function removeChatContactLoose(userId: string, contactId: string): Promise<void> {
  if (!userId || !contactId) return;
  const ref = doc(db, 'users', userId, P2P_CONTACTS_SUBCOLLECTION, peerInviteTokenToContactDocId(contactId));
  try {
    await deleteDoc(ref);
  } catch {
    /* нет документа / сеть */
  }
}

/** Удаление документа контакта; ошибка сети/правил пробрасывается (для необратимых действий в UI). */
export async function deleteP2PContactDocument(userId: string, peerInviteToken: string): Promise<void> {
  if (!userId || !peerInviteToken.trim()) return;
  const ref = doc(
    db,
    'users',
    userId,
    P2P_CONTACTS_SUBCOLLECTION,
    peerInviteTokenToContactDocId(peerInviteToken.trim())
  );
  await deleteDoc(ref);
}
